import React from 'react'
import { Card } from 'plume-ui'
import { formatDate } from '@/lib/date'
import { formatAmount } from '@/lib/number'
import { CardInnerTitle } from '../../../../atoms/CardInnerTitle'
import { DescriptionList } from '../../../../atoms/DescriptionList'
import { DoubleColumnContent } from '../../../investor/DoubleColumnContent'
import { FinanceBasicInfoData } from '../../../../../types/api'

type Props = {
  info: FinanceBasicInfoData
  withoutDilutiveShares: boolean
}

export const CompanyFinanceInfo: React.FC<Props> = (props) => {
  const formattedCommonStock = formatAmount(props.info.commonStock)
  const formattedCapitalReserve = formatAmount(props.info.capitalReserve)
  const formattedCapitalAndReserve = formatAmount(props.info.capitalAndReserve)
  const formattedCapitalReduction = formatAmount(props.info.capitalReduction)
  const formattedTotalFunding = formatAmount(props.info.totalFunding)
  const formattedFullyDilutedLatestValuation = formatAmount(
    props.info.fullyDilutedLatestValuation
  )
  const formattedOtherFundigEquity = formatAmount(
    parseIntForAmount(props.info.otherFundigEquity)
  )
  const formattedOtherFundingDebt = formatAmount(
    parseIntForAmount(props.info.otherFundingDebt)
  )
  const postMoneyValuationLabel = props.withoutDilutiveShares
    ? '調達後評価額（潜在株を含まない）'
    : '調達後評価額（潜在株を含む）'
  return (
    <Card>
      <CardInnerTitle title="基本情報" />
      <DoubleColumnContent>
        <DescriptionList
          list={{
            資本準備金の有無: { text: props.info.capitalReserveStatus },
            資本金: {
              text: formattedCommonStock
                ? `${formattedCommonStock.value}${formattedCommonStock.unit}`
                : '',
            },
            資本準備金: {
              text: formattedCapitalReserve
                ? `${formattedCapitalReserve.value}${formattedCapitalReserve.unit}`
                : '',
            },
            '資本金+資本準備金': {
              text: formattedCapitalAndReserve
                ? `${formattedCapitalAndReserve.value}${formattedCapitalAndReserve.unit}`
                : '',
            },
            減資の有無: { text: props.info.capitalReductionStatus },
            減資額: {
              text: formattedCapitalReduction
                ? `${formattedCapitalReduction.value}${formattedCapitalReduction.unit}`
                : '',
            },
            'その他の調達 (equity)': {
              text:
                props.info.otherFundigEquity && formattedOtherFundigEquity
                  ? `${formattedOtherFundigEquity.value}${formattedOtherFundigEquity.unit}`
                  : '',
              help: '資本金、準備金に含まれていない新株予約権付社債、転換社債、\nConvertible Notesの発行等のequity投資の金額',
            },
            総調達額: {
              text: formattedTotalFunding
                ? `${formattedTotalFunding.value}${formattedTotalFunding.unit}`
                : '',
            },
            'その他の調達 (debt)': {
              text:
                props.info.otherFundingDebt && formattedOtherFundingDebt
                  ? `${formattedOtherFundingDebt.value}${formattedOtherFundingDebt.unit}`
                  : '',
              help: '短期借入(Loan)、長期借入(Debt)、\nリワード型のクラウドファンディング、普通社債等',
            },
          }}
        />
        <DescriptionList
          className="finance-section-right"
          list={{
            '登記簿情報 反映日': {
              text:
                props.info.fundingRoundLastUpdatedByRegistrationCertificate &&
                formatDate(
                  props.info.fundingRoundLastUpdatedByRegistrationCertificate,
                  'yyyy/MM/dd'
                ),
            },
            評価額算出ラウンド実施日: {
              text:
                props.info.lastValuationDate &&
                formatDate(props.info.lastValuationDate, 'yyyy/MM/dd'),
            },
            [postMoneyValuationLabel]: {
              text: formattedFullyDilutedLatestValuation
                ? `${formattedFullyDilutedLatestValuation.value}${formattedFullyDilutedLatestValuation.unit}`
                : '',
            },
            融資情報: {
              text: props.info.privateLoan,
              help: '銀行などからの融資・ローン・負債など',
            },
            決算月: { text: props.info.settlementMonth },
          }}
        />
      </DoubleColumnContent>
    </Card>
  )
}

const parseIntForAmount = (s: string | null) => {
  if (s) {
    return parseInt(s.replace(/,/g, ''), 10)
  }
  return null
}
